
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { EventBus } from '@/main';
import { get, post } from '@/services/http';
import type { IPurchaseOrder, IPurchaseOrderItem } from '@/models/purchase_order';
import type { IPurchaseReceivedItem } from '@/models/purchase_received';

interface VariantResult {
  variantId: number;
  inventoryItemId: number;
  sku: string;
  productName: string;
  variantTitle: string;
  productId: number | null;
  variantImage: string | null;
  price: number;
  barcode: string;
}

interface SearchResponse {
  data: VariantResult[];
}

const SEARCH_DEBOUNCE_MS = 300;

@Component
export default class RelinkShopifyVariantDialog extends Vue {
  @Prop({ type: Boolean, default: false }) public readonly value!: boolean;
  @Prop({ type: Object, default: null }) public readonly purchaseOrder!: IPurchaseOrder | null;
  @Prop({ type: Object, default: null }) public readonly purchaseOrderItem!: IPurchaseOrderItem | null;
  @Prop({ type: Object, default: null }) public readonly purchaseReceivedItem!: IPurchaseReceivedItem | null;

  private searchText = '';
  private results: VariantResult[] = [];
  private searching = false;
  private suggestion: VariantResult | null = null;
  private pendingConfirm: VariantResult | null = null;
  private submitting = false;
  private debounceTimer: number | null = null;
  private latestSearchToken = 0;

  get dialog(): boolean {
    return this.value;
  }

  set dialog(val: boolean) {
    this.$emit('input', val);
    if (!val) {
      this.resetState();
    }
  }

  get supplierName(): string {
    const supplier = (this.purchaseOrder as any)?.supplier;
    return supplier?.name || 'this supplier';
  }

  @Watch('value')
  private onValueChange(val: boolean): void {
    if (val) {
      this.resetState();
      this.loadSuggestion();
    }
  }

  @Watch('searchText')
  private onSearchTextChange(val: string): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    if (!val || !val.trim()) {
      // Bump the token so any in-flight runSearch response is rejected
      // before it can overwrite the cleared results. Also clear `searching`
      // immediately — the in-flight request's finally branch skips that
      // clear when the token mismatches, which would leave the spinner up.
      this.latestSearchToken += 1;
      this.results = [];
      this.searching = false;
      return;
    }
    this.debounceTimer = window.setTimeout(() => {
      this.runSearch(val.trim());
    }, SEARCH_DEBOUNCE_MS);
  }

  private resetState(): void {
    // Bump the token first so a fetch that's still in flight from the
    // previous open of the dialog can't repopulate results/searching after
    // we've cleared them.
    this.latestSearchToken += 1;
    this.searchText = '';
    this.results = [];
    this.suggestion = null;
    this.pendingConfirm = null;
    this.submitting = false;
    this.searching = false;
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private async loadSuggestion(): Promise<void> {
    const oldSku = this.purchaseOrderItem?.sku;
    const oldVariantId = this.purchaseOrderItem?.variantId;
    if (!oldSku) {
      this.suggestion = null;
      return;
    }

    // Capture the current session token. resetState() bumps it whenever the
    // dialog is reopened, so a stale response from a previous PO line will
    // be discarded instead of clobbering the new session's suggestion.
    const token = this.latestSearchToken;

    try {
      const escaped = String(oldSku).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const response = await get<SearchResponse>(
        `/shopify/product-variants/search?q=${encodeURIComponent(`sku:"${escaped}"`)}&limit=1`,
      );
      if (token !== this.latestSearchToken) {
        return;
      }
      const first = response?.data?.[0];
      if (!first) {
        this.suggestion = null;
        return;
      }
      if (oldVariantId != null && Number(first.variantId) === Number(oldVariantId)) {
        this.suggestion = null;
        return;
      }
      this.suggestion = first;
    } catch (err) {
      // Suggestion is best-effort; failure here should not block the picker.
      if (token !== this.latestSearchToken) {
        return;
      }
      this.suggestion = null;
    }
  }

  private async runSearch(query: string): Promise<void> {
    const token = ++this.latestSearchToken;
    this.searching = true;
    try {
      const response = await get<SearchResponse>(
        `/shopify/product-variants/search?q=${encodeURIComponent(query)}&limit=20`,
      );
      if (token !== this.latestSearchToken) {
        return;
      }
      this.results = response?.data || [];
    } catch (err: any) {
      if (token !== this.latestSearchToken) {
        return;
      }
      this.results = [];
      EventBus.$emit('show-snackbar', {
        message: `Search failed: ${err?.message || 'unknown error'}`,
        color: 'error',
      });
    } finally {
      if (token === this.latestSearchToken) {
        this.searching = false;
      }
    }
  }

  private selectVariant(variant: VariantResult): void {
    this.pendingConfirm = variant;
  }

  /**
   * US-094: the same dialog now serves two cases.
   *
   * Post-receive (US-066) re-points a receive line whose Shopify push failed,
   * so it carries a purchaseReceivedItem. Pre-receive has none — the receive
   * was rejected before it committed because every line pointed at a deleted
   * variant, so there is no receive row to re-point and the merchant is fixing
   * the PO line so the FIRST receive can go through. The backend reads the
   * absent path segment as exactly that.
   */
  private get relinkUrl(): string | null {
    if (!this.purchaseOrder || !this.purchaseOrderItem) return null;
    const base = `/purchase_orders/${this.purchaseOrder.id}/items/${this.purchaseOrderItem.id}`;
    return this.purchaseReceivedItem
      ? `${base}/received-items/${this.purchaseReceivedItem.id}/relink-shopify-variant`
      : `${base}/relink-shopify-variant`;
  }

  private async confirmRelink(): Promise<void> {
    if (!this.pendingConfirm || !this.purchaseOrder || !this.purchaseOrderItem) {
      return;
    }
    this.submitting = true;
    // Capture old + new identifiers BEFORE the dialog closes — closing
    // triggers resetState() which clears pendingConfirm.
    const oldName = (this.purchaseOrderItem.productName || '').trim();
    const oldSku = (this.purchaseOrderItem.sku || '').trim();
    const newSku = (this.pendingConfirm.sku || '').trim();
    const newName = (this.pendingConfirm.productName || '').trim();
    try {
      const url = this.relinkUrl as string;
      const body = {
        newVariantId: this.pendingConfirm.variantId,
        newInventoryItemId: this.pendingConfirm.inventoryItemId,
        newSku: this.pendingConfirm.sku,
        newProductName: this.pendingConfirm.productName,
        newVariantTitle: this.pendingConfirm.variantTitle,
      };
      const response = await post<{ data: any }>(url, body);
      this.$emit('relinked', response?.data);
      this.dialog = false;
      EventBus.$emit('show-snackbar', {
        message: this.buildSuccessMessage(oldName, oldSku, newName, newSku),
        color: 'success',
      });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to relink variant';
      EventBus.$emit('show-snackbar', { message, color: 'error' });
    } finally {
      this.submitting = false;
    }
  }

  private buildSuccessMessage(oldName: string, oldSku: string, newName: string, newSku: string): string {
    const oldLabel = this.formatItemLabel(oldName, oldSku);
    const newLabel = this.formatItemLabel(newName, newSku);
    // US-094: pre-receive has nothing to retry — no inventory was ever pushed,
    // because the receive was rejected before it committed. Telling the
    // merchant to click Retry Sync there sends them looking for a button that
    // only exists on a recorded receipt.
    const nextStep = this.purchaseReceivedItem
      ? 'Click Retry Sync to push inventory to Shopify.'
      : 'Click Receive again to receive this line.';
    if (!oldLabel) {
      return `Variant relinked. ${nextStep}`;
    }
    if (newLabel && newLabel !== oldLabel) {
      return `Relinked ${oldLabel} → ${newLabel}. ${nextStep}`;
    }
    return `Relinked ${oldLabel} to a fresh Shopify variant. ${nextStep}`;
  }

  private formatItemLabel(name: string, sku: string): string {
    if (name && sku) {
      return `${name} (SKU: ${sku})`;
    }
    if (name) {
      return name;
    }
    if (sku) {
      return `SKU ${sku}`;
    }
    return '';
  }
}
